import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaletteSection } from "@/components/PaletteSection";
import { Clock, Link2, Unlink, CheckCircle2, AlertTriangle, Shield, Settings as Cog, ExternalLink } from "lucide-react";

export default function Instellingen() {
  const { user, company, refresh } = useAuth();
  const isOwner = user?.role === "OWNER";
  const settings = company?.settings || {};

  const [workStart, setWorkStart] = useState(settings.workday_start || "07:00");
  const [workEnd, setWorkEnd] = useState(settings.workday_end || "17:00");
  const [autoStop, setAutoStop] = useState(settings.auto_stop_hour ?? 17);
  const [savingWork, setSavingWork] = useState(false);

  useEffect(() => {
    setWorkStart(settings.workday_start || "07:00");
    setWorkEnd(settings.workday_end || "17:00");
    setAutoStop(settings.auto_stop_hour ?? 17);
  }, [settings.workday_start, settings.workday_end, settings.auto_stop_hour]);

  const saveWork = async (e) => {
    e.preventDefault();
    if (!isOwner) return;
    setSavingWork(true);
    try {
      await api.patch("/company/settings", {
        workday_start: workStart,
        workday_end: workEnd,
        auto_stop_hour: Number(autoStop),
      });
      toast.success("Werktijden opgeslagen");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon niet opslaan");
    } finally { setSavingWork(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Beheer</div>
        <h1 className="pb-heading text-3xl md:text-4xl font-black">INSTELLINGEN</h1>
      </div>

      {/* ─────────── Werktijden ─────────── */}
      <section className="pb-card p-5" data-testid="settings-workday">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={20} className="text-orange-600"/>
          <h2 className="pb-heading text-xl font-black">Standaard werktijden</h2>
        </div>
        <p className="text-sm text-slate-600 font-medium mb-4">
          Deze tijden worden automatisch ingevuld als een planner een nieuwe klus op het planbord zet.
          De auto-stop zorgt dat lopende timers 's avonds automatisch worden gesloten.
        </p>
        <form onSubmit={saveWork} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Starttijd</div>
            <input type="time" className="pb-input" value={workStart} onChange={(e) => setWorkStart(e.target.value)} disabled={!isOwner} data-testid="settings-workday-start"/>
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Eindtijd</div>
            <input type="time" className="pb-input" value={workEnd} onChange={(e) => setWorkEnd(e.target.value)} disabled={!isOwner} data-testid="settings-workday-end"/>
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Auto-stop (uur, 24u)</div>
            <input type="number" min="0" max="23" className="pb-input" value={autoStop} onChange={(e) => setAutoStop(e.target.value)} disabled={!isOwner} data-testid="settings-auto-stop"/>
          </label>
          <div className="md:col-span-3 flex justify-end">
            <button className="pb-btn-primary" disabled={!isOwner || savingWork} data-testid="settings-workday-save">
              {savingWork ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        </form>
        {!isOwner && <div className="mt-3 text-xs text-slate-500 italic flex items-center gap-1"><Shield size={12}/> Alleen de eigenaar kan werktijden wijzigen.</div>}
      </section>

      <PaletteSection />

      {/* ─────────── Notion ─────────── */}
      <NotionSection isOwner={isOwner}/>
    </div>
  );
}

function NotionSection({ isOwner }) {
  const [status, setStatus] = useState({ connected: false, database_id: "", field_map: {}, mappable_fields: [] });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [dbId, setDbId] = useState("");
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/notion/status");
      setStatus(r.data);
    } catch (_) { /* niet gekoppeld */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/integrations/notion/connect", {
        token: token.trim(),
        database_id: dbId.trim(),
      });
      toast.success("Notion gekoppeld! Nu kolommen koppelen hieronder.");
      setToken(""); setDbId("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Koppeling mislukt — check je token en database-ID.");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Notion ontkoppelen? Projecten blijven in Ploegbord staan.")) return;
    setSaving(true);
    try {
      await api.post("/integrations/notion/disconnect");
      toast.success("Notion ontkoppeld");
      await load();
    } finally { setSaving(false); }
  };

  return (
    <section className="pb-card p-5" data-testid="settings-notion">
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={20} className="text-orange-600"/>
        <h2 className="pb-heading text-xl font-black">Notion-koppeling</h2>
      </div>
      <p className="text-sm text-slate-600 font-medium mb-4">
        Haal projecten op uit je Notion-database. Werkt met jouw eigen workspace — jij bepaalt welke Notion-kolommen aan welke Ploegbord-velden gekoppeld worden.
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Laden…</div>
      ) : status.connected ? (
        <>
          <div className="rounded border-2 border-green-600 bg-green-50 p-4 mb-4" data-testid="notion-connected">
            <div className="flex items-center gap-2 text-green-800 font-black">
              <CheckCircle2 size={18}/> Gekoppeld
            </div>
            <div className="text-xs text-slate-600 font-mono mt-1 break-all">DB: {status.database_id}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href="/projecten" className="pb-btn-outline flex items-center gap-1" data-testid="notion-goto-import">
                <ExternalLink size={14}/> Ga naar Projecten om te importeren
              </a>
              {isOwner && (
                <button onClick={disconnect} disabled={saving} className="pb-btn-outline !border-red-600 !text-red-700 flex items-center gap-1" data-testid="notion-disconnect">
                  <Unlink size={14}/> Ontkoppelen
                </button>
              )}
            </div>
          </div>
          <NotionMapper isOwner={isOwner} onSaved={load}/>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            className="w-full text-left mb-3 p-3 border-2 border-orange-300 rounded bg-orange-50 text-sm font-bold text-orange-900 flex items-center justify-between"
            data-testid="notion-guide-toggle"
          >
            <span>📖 Hoe krijg ik mijn Notion-token en database-ID? {showGuide ? "▲" : "▼"}</span>
          </button>
          {showGuide && (
            <ol className="list-decimal list-inside text-sm text-slate-700 font-medium space-y-2 mb-4 pl-2" data-testid="notion-guide">
              <li>Ga naar <a className="text-orange-700 underline font-bold" href="https://www.notion.so/profile/integrations" target="_blank" rel="noreferrer">notion.so/profile/integrations</a> → <b>New integration</b>.</li>
              <li>Naam = "Ploegbord", kies je workspace, <b>Submit</b>.</li>
              <li>Kopieer het <b>Internal Integration Secret</b> (begint met <code>secret_</code> of <code>ntn_</code>).</li>
              <li>Open je projecten-database in Notion → <b>⋯ → Connections → Connect to</b> → kies Ploegbord.</li>
              <li>Kopieer de <b>database-ID</b> uit de URL (32 tekens tussen laatste <code>/</code> en <code>?</code>).</li>
              <li>Plak beide hieronder → <b>Koppelen</b>. In de volgende stap kies je zelf welke Notion-kolommen aan welke Ploegbord-velden gekoppeld worden.</li>
              <li className="text-slate-600 italic">Verplicht per project zijn: <b>Naam</b> (title), <b>Adres</b>, <b>Postcode</b>, <b>Plaats</b> en <b>Omschrijving</b>. De namen van je kolommen mag je zelf kiezen.</li>
            </ol>
          )}
          <form onSubmit={connect} className="space-y-3">
            <label className="block">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Notion Integration Token</div>
              <input type="password" className="pb-input font-mono" placeholder="secret_… of ntn_…" value={token} onChange={(e) => setToken(e.target.value)} required data-testid="notion-token-input"/>
            </label>
            <label className="block">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Database-ID (32 tekens)</div>
              <input className="pb-input font-mono" placeholder="abc123def456…" value={dbId} onChange={(e) => setDbId(e.target.value)} required data-testid="notion-dbid-input"/>
            </label>
            {!isOwner ? (
              <div className="text-xs text-slate-500 italic flex items-center gap-1"><Shield size={12}/> Alleen de eigenaar kan Notion koppelen.</div>
            ) : (
              <button type="submit" disabled={saving} className="pb-btn-primary flex items-center gap-2" data-testid="notion-connect-btn">
                <Link2 size={16}/> {saving ? "Testen…" : "Koppelen"}
              </button>
            )}
          </form>
        </>
      )}

      {!loading && !status.connected && (
        <div className="mt-4 text-xs text-slate-500 flex items-center gap-1">
          <AlertTriangle size={12}/> Geen Notion? Je kunt ook gewoon projecten in Ploegbord zelf blijven aanmaken.
        </div>
      )}
    </section>
  );
}

function NotionMapper({ isOwner, onSaved }) {
  const [schema, setSchema] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/notion/schema");
      setSchema(r.data);
      setMapping(r.data.field_map || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon Notion-schema niet laden");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!isOwner) return;
    const required = (schema?.mappable_fields || []).filter((f) => f.required);
    const missing = required.filter((f) => !(mapping[f.key] || "").trim());
    if (missing.length) {
      toast.error(`Verplicht: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/integrations/notion/mapping", { field_map: mapping });
      toast.success("Veldkoppeling opgeslagen");
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500">Notion-kolommen ophalen…</div>;
  if (!schema) return null;

  const columnsByType = (type) => (schema.columns || []).filter((c) => c.type === type);

  return (
    <div className="border-2 border-slate-200 rounded-lg p-4" data-testid="notion-mapper">
      <div className="flex items-center gap-2 mb-1">
        <Cog size={16} className="text-orange-600"/>
        <h3 className="pb-heading text-lg font-black">Welke Notion-kolom hoort bij welk Ploegbord-veld?</h3>
      </div>
      <p className="text-xs text-slate-600 font-medium mb-4">
        Database: <b>{schema.database_title}</b> · {schema.columns.length} kolommen gevonden.
        Verplichte velden zijn met * gemarkeerd.
      </p>
      <div className="space-y-2">
        {(schema.mappable_fields || []).map((f) => {
          const compatible = columnsByType(f.type);
          const current = mapping[f.key] || "";
          const currentExists = (schema.columns || []).some((c) => c.name === current);
          return (
            <div key={f.key} className="grid grid-cols-1 md:grid-cols-3 gap-2 md:items-center" data-testid={`notion-map-row-${f.key}`}>
              <div className="text-sm font-bold text-slate-800">
                {f.label}{f.required && <span className="text-red-600"> *</span>}
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{f.type}</div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <select
                  className="pb-input flex-1"
                  value={current}
                  onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                  disabled={!isOwner}
                  data-testid={`notion-map-${f.key}`}
                >
                  <option value="">— Niet gekoppeld{f.required ? " (verplicht!)" : ""} —</option>
                  {compatible.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                  {/* Val-terug: huidige waarde die geen bekend type heeft, blijft kiesbaar */}
                  {current && !compatible.some((c) => c.name === current) && (
                    <option value={current}>{current} {currentExists ? "(ander type)" : "(niet gevonden)"}</option>
                  )}
                </select>
                {compatible.length === 0 && (
                  <div className="text-[11px] text-orange-700 font-bold whitespace-nowrap" title={`Geen Notion-kolom van type '${f.type}' gevonden`}>
                    ⚠ geen "{f.type}" kolom
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {isOwner && (
        <div className="mt-4 flex justify-end">
          <button onClick={save} disabled={saving} className="pb-btn-primary" data-testid="notion-mapping-save">
            {saving ? "Opslaan…" : "Veldkoppeling opslaan"}
          </button>
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 italic">
        Tip: wijzig je kolomnamen in Notion dan hoef je alleen hier opnieuw te koppelen — geen code nodig.
      </div>
    </div>
  );
}
